/*
 * The due diligence workflow as a whole (states, schedules, timeframes, the Final Review, and archives), shared by TPRM and UCRM.
 */
import type { AssessmentSubjectBase } from './AssessmentSubject'; // Type-only, to avoid a runtime import cycle (`AssessmentSubject` imports values from this file).
import { DueDiligenceAssessmentBase } from './DueDiligence';
import { ExceptionHistoryModificationsResponse } from './Exceptions';
import { FileUpdates, UploadedFile } from './Files';
import { InherentRiskQuestionnaireResponse, SubmitInherentRiskQuestionnaireSelectionsBase } from './InherentRisk';
import { IssueHistoryModificationsResponse } from './Issues';
import { Effectiveness } from './OperationalControls';
import { RiskRating } from './RiskRating';
import { ScheduleFrequency } from './ScheduleFrequency';

/**
 * AWAITING_ASSESSMENT -> EVALUATING_INHERENT_RISK when the user clicks the button in the UI to start a new due diligence workflow. At this time, a new IRQ is generated (answers are preserved where possible).
 * EVALUATING_INHERENT_RISK -> PERFORMING_DUE_DILIGENCE when the user submits the IRQ. At this time, new assessments are generated (answers are preserved where possible).
 * PERFORMING_DUE_DILIGENCE -> AWAITING_ASSESSMENT when the user submits the Final Review. At this time, the assessment subject / its workflow are copied into a historical object.
 */
export enum AssessmentWorkflowState {
    AWAITING_ASSESSMENT = 'Awaiting Assessment',
    EVALUATING_INHERENT_RISK = 'Evaluating Inherent Risk',
    PERFORMING_DUE_DILIGENCE = 'Performing Due Diligence',
}

// Matches the API's `ScheduleV2`. Rename both (removing the V2) once the API's deprecated `Schedule` is eliminated.
export type ScheduleV2 = {
    schedule_number: number;
    schedule_frequency: ScheduleFrequency;
};

/**
 * Sets the due diligence workflow due date and the IRQ due date when starting OR restarting the due diligence workflow. Omitting a date removes it.
 */
export interface StartAssessmentWorkflowRequest {
    due_diligence_workflow_due_date?: string;
    irq_target_completion_date?: string;
}

/** Uses months. */
export interface ReassessmentIntervals {
    low?: number;
    low_moderate?: number;
    moderate?: number;
    moderate_high?: number;
    high?: number;
}

export interface DueDiligenceTimeframes {
    expected_days_to_complete_final_review?: number;
    expected_days_to_complete_irq?: number;
    service_reassessment_intervals: ReassessmentIntervals;
}

export interface ExpectedDurationsForAssessment {
    assessment_id: string;
    expected_days_to_complete_assessment?: number;
    expected_days_to_complete_questionnaire?: number;
}

export interface UpdateDueDiligenceTimeframesRequest {
    expected_durations_for_assessments: ExpectedDurationsForAssessment[];
    timeframes: DueDiligenceTimeframes;
}

export interface SaveOrSubmitFinalReviewRequest {
    is_submit: boolean;
    file_updates: FileUpdates;

    comments?: string;
    new_assessment_due_date?: string;
    new_irq_target_completion_date?: string;
    overall_control_effectiveness?: Effectiveness;
    residual_risk?: RiskRating;
}

/** The members shared by `CompleteTprmIntakeRequest` (TPRM) and `CompleteUcrmIntakeRequest` (UCRM). Extending interfaces narrow `submit_irq_selections` to their module's type. */
export interface CompleteIntakeRequestBase {
    organization_id: string;
    submit_assessment_subject: boolean;
    submit_irq: boolean;
    submit_irq_selections: SubmitInherentRiskQuestionnaireSelectionsBase; // Ignored if `submit_irq` is false.
    submit_organization: boolean;

    assessment_subject_id?: string;
    due_diligence_workflow_due_date?: string; // Ignored if the user does not have the necessary role or there is no finalized assessment subject.
    irq_target_completion_date?: string; // Allows the user to set the IRQ due date when the assessment subject is being finalized but the IRQ is not. Ignored if the user does not have the necessary role, or there is no finalized assessment subject, or if `submit_irq` is true.
}

/** The members shared by the TPRM and UCRM snapshots taken when a due diligence workflow is completed. Extending interfaces declare the module-specific organization (`third_party` in TPRM, `business` in UCRM), assessment subject (`service` in TPRM, `use_case` in UCRM), and `assessments`. */
export interface DueDiligenceWorkflowArchiveBase {
    assessment_submitter_id: string;
    /** A snapshot of the organization's exceptions at the time a Final Review was submitted. We have not always recorded these snapshots; this list is always empty for legacy historical assessments. */
    exceptions: ExceptionHistoryModificationsResponse[];
    irq: InherentRiskQuestionnaireResponse;
    /** A snapshot of the organization's issues at the time a Final Review was submitted. We have not always recorded these snapshots; this list is always empty for legacy historical assessments. */
    issues: IssueHistoryModificationsResponse[];
    timestamp: string;
}

export enum DueDiligenceWorkflowFileSource {
    FINAL_REVIEW = 'Final Review',
    INHERENT_RISK_QUESTIONNAIRE = 'Inherent Risk Questionnaire',
    SUMMIT_GUIDE_ASSESSMENT_WIDE_FILE = 'SummIT Guide™ Assessment-Wide Files',
    QUESTIONNAIRE = 'Due Diligence Questionnaire',
    QUESTIONNAIRE_CONTROL_ASSESSMENT = 'Due Diligence Assessment',
}

export interface DueDiligenceWorkflowFileWithSource extends UploadedFile {
    source: DueDiligenceWorkflowFileSource;
}

/**
 * Categorizes all files from a historical due diligence workflow (TPRM or UCRM) by source.
 * If there are multiple files with the same ID (which will happen if the client or a questionnaire respondent leverages the ability to map a file to multiple controls/questions), only the first instance encountered will be included in the list.
 * Note that the user may still see multiple files with the same name. We can't know whether two files have the same content even if they have the same name. It may even be the case that the client and a respondent have uploaded entirely different files that happen to have the same names.
 */
export const categorizeDueDiligenceWorkflowFiles = (assessment: DueDiligenceWorkflowArchiveBase & { assessments: DueDiligenceAssessmentBase[] }, assessmentSubject: Pick<AssessmentSubjectBase, 'assessment_workflow_data'>): DueDiligenceWorkflowFileWithSource[] => {
    const seenFileIds = new Set<string>();
    const files: DueDiligenceWorkflowFileWithSource[] = [];

    const addFileToListIfNew = (file: DueDiligenceWorkflowFileWithSource) => {
        if (!seenFileIds.has(file.file_id)) {
            files.push(file);
            seenFileIds.add(file.file_id);
        }
    };

    assessment.irq.sections.forEach((section) => {
        section.files.forEach((file) => {
            addFileToListIfNew({
                ...file,
                source: DueDiligenceWorkflowFileSource.INHERENT_RISK_QUESTIONNAIRE,
            });
        });
    });

    assessment.assessments.forEach((assessment) =>
        assessment.control_frameworks.forEach((framework) =>
            framework.control_groups.forEach((group) =>
                group.controls.forEach((control) => {
                    control.control_assessment_human.files.forEach((file) => {
                        addFileToListIfNew({
                            ...file,
                            source: DueDiligenceWorkflowFileSource.QUESTIONNAIRE_CONTROL_ASSESSMENT,
                        });
                    });

                    control.questions.forEach((question) => {
                        question.files.forEach((file) => {
                            addFileToListIfNew({
                                ...file,
                                source: DueDiligenceWorkflowFileSource.QUESTIONNAIRE,
                            });
                        });
                    });
                })
            )
        )
    );

    assessment.assessments.forEach((assessment) => {
        assessment.assessment_review_human.files.forEach((file) => {
            addFileToListIfNew({
                ...file,
                source: DueDiligenceWorkflowFileSource.QUESTIONNAIRE_CONTROL_ASSESSMENT,
            });
        });

        assessment.assessment_wide_files.forEach((file) => {
            addFileToListIfNew({
                ...file,
                source: DueDiligenceWorkflowFileSource.SUMMIT_GUIDE_ASSESSMENT_WIDE_FILE,
            });
        });
    });

    assessmentSubject.assessment_workflow_data.final_review_human_assessment.files.forEach((file) => {
        addFileToListIfNew({
            ...file,
            source: DueDiligenceWorkflowFileSource.FINAL_REVIEW,
        });
    });

    return files;
};

export interface DueDiligenceWorkflowProgress {
    completedStepNumber: number;
    inProgressStepDescription: string;
    steps: string[];
}

export enum IntakeWorkflowStep {
    ORGANIZATION = 0,
    ASSESSMENT_SUBJECT = 1,
    IRQ = 2,
    CHOOSE_SUBMISSIONS = 3,
    COMPLETE = 4,
}

/** The state of the TPRM/UCRM Intake page, which is persisted in the URL. */
export interface IntakeUrlState {
    completingIntake: boolean;
    step: IntakeWorkflowStep;
    organizationId?: string;
    assessmentSubjectId?: string;
    organizationActions: {
        created: boolean;
        saved: boolean;
        submitted: boolean;
    };
    assessmentSubjectActions: {
        skipped: boolean;
        created: boolean;
        saved: boolean;
        submitted: boolean;
    };
    irqActions: {
        skipped: boolean;
        saved: boolean;
        submitted: boolean;
    };
}
